import { useEffect, useRef, useState } from 'react';

/**
 * Full-screen brand splash shown while a template opens.
 *
 * - `persistent` (page data-fetch phase): shows until the parent unmounts it.
 * - timed (default, editor mount phase): fades out ~0.5s before `durationMs`,
 *   then calls onDone(). Runs its timers once, independent of parent re-renders.
 *
 * The two phases render identically, so the hand-off (fetch splash → editor
 * splash) looks like one continuous screen.
 */
export default function SplashScreen({ onDone, durationMs = 3000, persistent = false }) {
    const [leaving, setLeaving] = useState(false);
    const doneRef = useRef(onDone);
    doneRef.current = onDone;

    useEffect(() => {
        if (persistent) return undefined; // stays visible until the parent removes it
        const fadeAt = Math.max(0, durationMs - 500);
        const t1 = setTimeout(() => setLeaving(true), fadeAt);
        const t2 = setTimeout(() => doneRef.current?.(), durationMs);
        return () => { clearTimeout(t1); clearTimeout(t2); };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- run once; duration/persistent fixed at mount
    }, [persistent]);

    return (
        <div
            className={`fixed inset-0 z-[200] flex items-center justify-center select-none bg-white dark:bg-[#1e1e1e]
                transition-opacity duration-500 ease-out ${leaving ? 'opacity-0' : 'opacity-100'}`}
            style={{ fontFamily: 'Inter, "Noto Sans Bengali", system-ui, sans-serif' }}
        >
            <style>{`
                @keyframes skj-splash-bar {
                    0% { left: -35% }
                    100% { left: 100% }
                }
            `}</style>

            <div className="flex flex-col items-center gap-6">
                {/* Brand logo — light / dark variants match the editor's own theming */}
                <img src="/images/logo.svg" alt="SohozKaj" draggable="false" className="h-14 w-auto dark:hidden" />
                <img src="/images/logo-white.svg" alt="SohozKaj" draggable="false" className="h-14 w-auto hidden dark:block" />

                {/* Indeterminate progress bar */}
                <div className="relative h-1 w-40 overflow-hidden rounded-full bg-black/10 dark:bg-white/15">
                    <span
                        className="absolute inset-y-0 w-[35%] rounded-full bg-[#0d99ff]"
                        style={{ animation: 'skj-splash-bar 1.1s ease-in-out infinite' }}
                    />
                </div>

                <span className="text-[11px] text-black/40 dark:text-white/40">Loading your template…</span>
            </div>
        </div>
    );
}
